'use client';
import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Link from 'next/link';
import { ArrowLeft, Copy, Download, Check, Merge } from 'lucide-react';

export default function DataMergerPage() {
  const [csvInput1, setCsvInput1] = useState('');
  const [csvInput2, setCsvInput2] = useState('');
  const [csvOutput, setCsvOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const mergeData = () => {
    try {
      setError('');
      if (!csvInput1.trim() || !csvInput2.trim()) {
        setError('Please enter both CSV datasets');
        return;
      }

      const lines1 = csvInput1.trim().split('\n');
      const lines2 = csvInput2.trim().split('\n');
      
      // Assume both have headers and we want to append lines2 (without header) to lines1
      const header1 = lines1[0];
      const data2 = lines2.slice(1);

      setCsvOutput([...lines1, ...data2].join('\n'));
    } catch (err) {
      setError('Error merging data. Please check your input format.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCsv = () => {
    const blob = new Blob([csvOutput], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'merged_data.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                CSV Data Merger
              </h1>
            <p className="text-lg text-muted-foreground">
              Merge two CSV datasets into one instantly
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">CSV Dataset 1</label>
              <Textarea
                placeholder="Header1,Header2&#10;Data1,Data2"
                value={csvInput1}
                onChange={(e) => setCsvInput1(e.target.value)}
                className="min-h-[150px] font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">CSV Dataset 2</label>
              <Textarea
                placeholder="Header1,Header2&#10;Data3,Data4"
                value={csvInput2}
                onChange={(e) => setCsvInput2(e.target.value)}
                className="min-h-[150px] font-mono text-sm"
              />
            </div>
          </div>

          <div className="space-y-4">
            <Button 
              onClick={mergeData} 
              className="w-full"
              size="lg"
            >
              <Merge className="w-4 h-4 mr-2" />
              Merge Datasets
            </Button>
            {error && (
              <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Merged Output</label>
              <Textarea
                value={csvOutput}
                readOnly
                placeholder="Your merged data will appear here..."
                className="min-h-[200px] font-mono text-sm bg-muted"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={copyToClipboard} 
                className="flex-1"
                variant="outline"
                disabled={!csvOutput}
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button 
                onClick={downloadCsv} 
                className="flex-1"
                variant="outline"
                disabled={!csvOutput}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Data Merger FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. Do the headers need to match?</p><p className="text-muted-foreground">For best results, yes. The tool appends the second dataset to the first, assuming they share the same structure.</p></div>
          <div><p className="font-semibold">2. What happens to the header of the second dataset?</p><p className="text-muted-foreground">The tool automatically removes the first line (header) of the second dataset before merging.</p></div>
          <div><p className="font-semibold">3. Can I merge more than two datasets?</p><p className="text-muted-foreground">Currently, the tool supports merging two datasets at a time. You can merge the result with a third dataset if needed.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
